import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  updateDoc,
  where,
  runTransaction,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { CONT_COLLECTIONS } from "./collections";
import { registrarDocumentoComercial } from "./documentosComercialesService";
import { registrarAsientoVenta } from "./asientosService";
import { obtenerSiguienteCorrelativo } from "./correlativosService";
import { registrarDocumentoTributarioVenta } from "./tributarioService";
import { assertPeriodoAbiertoTx } from "./accountingCore";

const db = getFirestore(firebaseApp);

const txt = (v) => (v == null ? "" : String(v).trim());
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Number(n.toFixed(2)) : 0;
};

export const obtenerClientesDesdeCompany = async ({ rucEmpresa, empresasHabilitadas = [] } = {}) => {
  const rucActual = txt(rucEmpresa);
  const habilitadas = Array.isArray(empresasHabilitadas)
    ? new Set(empresasHabilitadas.map((x) => txt(x)).filter(Boolean))
    : null;

  const snap = await getDocs(collection(db, "company"));
  const clientes = snap.docs
    .map((d) => {
      const data = d.data() || {};
      const ruc = txt(d.id);
      return {
        ruc,
        razon_social: txt(data.name || data.razonSocial || data.nombreComercial || ruc),
        nombre_comercial: txt(data.nombreComercial || data.name || ""),
        activo: data.activo !== false,
      };
    })
    .filter((c) => c.ruc && c.ruc !== rucActual)
    .filter((c) => (habilitadas && habilitadas.size > 0 ? habilitadas.has(c.ruc) : true))
    .sort((a, b) => String(a.razon_social || "").localeCompare(String(b.razon_social || "")));

  return clientes;
};

export const obtenerOrdenesVentaPorEmpresa = async (rucEmpresa) => {
  const ruc = txt(rucEmpresa);
  if (!ruc) return [];
  const q = query(collection(db, CONT_COLLECTIONS.ORDENES_VENTA), where("ruc_empresa", "==", ruc));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.fecha || "").localeCompare(String(a.fecha || "")));
};

export const registrarOrdenVenta = async ({ rucEmpresa, orden, usuarioRegistro }) => {
  const ruc = txt(rucEmpresa);
  if (!ruc) throw new Error("Empresa inválida para registrar orden de venta.");

  const { correlativo, secuencia } = await obtenerSiguienteCorrelativo({
    rucEmpresa: ruc,
    prefijo: "OS",
  });

  const lineas = Array.isArray(orden?.lineas)
    ? orden.lineas
        .map((l, idx) => ({
          descripcion: txt(l?.descripcion),
          indice: idx,
          cantidad: num(l?.cantidad),
          precio_unitario: num(l?.precioUnitario ?? l?.precio_unitario),
          importe: num(l?.importe),
        }))
        .filter((l) => l.descripcion && l.cantidad > 0 && l.precio_unitario > 0)
    : [];
  const totalLineas = lineas.reduce((acc, l) => acc + num(l.importe || (l.cantidad * l.precio_unitario)), 0);
  const total = totalLineas > 0 ? num(totalLineas) : num(orden?.total);
  if (total <= 0) throw new Error("La orden de venta debe tener al menos una línea con importe mayor a cero.");

  const clienteRuc = txt(orden?.clienteRuc);
  const clienteRazonSocial = txt(orden?.clienteRazonSocial);
  if (!clienteRuc || !clienteRazonSocial) {
    throw new Error("La OS debe tener RUC y razón social del cliente.");
  }

  const payload = {
    ruc_empresa: ruc,
    correlativo,
    secuencia,
    fecha: txt(orden?.fecha),
    cliente_ruc: clienteRuc,
    cliente_razon_social: clienteRazonSocial,
    descripcion: txt(orden?.descripcion),
    lineas,
    moneda: txt(orden?.moneda || "PEN"),
    total,
    total_facturado: 0,
    saldo_pendiente: total,
    estado_facturacion: "PENDIENTE",
    estado: "REGISTRADO",
    documentos_relacionados: [],
    usuario_registro: txt(usuarioRegistro),
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, CONT_COLLECTIONS.ORDENES_VENTA), payload);

  const docId = await registrarDocumentoComercial({
    rucEmpresa: ruc,
    tipoDocumento: "ORDEN_VENTA",
    serie: "OS",
    numero: correlativo,
    fecha: payload.fecha,
    monto: total,
    moneda: payload.moneda,
    moduloOrigen: "GESTION_VENTAS",
    entidadId: ref.id,
    entidadTipo: "ORDEN_VENTA",
    metadata: {
      cliente_ruc: payload.cliente_ruc,
      cliente_razon_social: payload.cliente_razon_social,
    },
  });

  await updateDoc(doc(db, CONT_COLLECTIONS.ORDENES_VENTA, ref.id), {
    documentos_relacionados: [{ id: docId, tipo: "ORDEN_VENTA" }],
    updated_at: serverTimestamp(),
  });

  return ref.id;
};

export const obtenerComprobantesVentaPorEmpresa = async (rucEmpresa) => {
  const ruc = txt(rucEmpresa);
  if (!ruc) return [];
  const q = query(collection(db, CONT_COLLECTIONS.COMPROBANTES_VENTA), where("ruc_empresa", "==", ruc));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.fecha_emision || "").localeCompare(String(a.fecha_emision || "")));
};

export const registrarComprobanteVenta = async ({ rucEmpresa, comprobante, usuarioRegistro }) => {
  const ruc = txt(rucEmpresa);
  if (!ruc) throw new Error("Empresa inválida para registrar comprobante de venta.");

  const lineas = Array.isArray(comprobante?.lineas)
    ? comprobante.lineas
        .map((l) => ({
          descripcion: txt(l?.descripcion),
          linea_os_id: txt(l?.lineaOsId || l?.linea_os_id),
          indice_os: Number.isFinite(Number(l?.indiceOs ?? l?.indice_os)) ? Number(l?.indiceOs ?? l?.indice_os) : null,
          cantidad: num(l?.cantidad),
          precio_unitario: num(l?.precioUnitario ?? l?.precio_unitario),
          importe: num(l?.importe),
        }))
        .filter((l) => l.descripcion && l.cantidad > 0 && l.precio_unitario > 0)
    : [];
  if (lineas.length === 0) {
    throw new Error("Debe registrar al menos una línea válida en la factura/boleta.");
  }

  const subtotalLineas = lineas.reduce((acc, l) => acc + num(l.importe || (l.cantidad * l.precio_unitario)), 0);
  const totalComprobante = num(comprobante?.total);
  const igvComprobante = num(comprobante?.igv);

  let subtotal = num(subtotalLineas);
  let igv = num(igvComprobante || subtotal * 0.18);
  let total = num(subtotal + igv);

  if (totalComprobante > 0) {
    total = totalComprobante;
    if (igvComprobante > 0) {
      subtotal = num(Math.max(0, total - igvComprobante));
      igv = num(total - subtotal);
    } else {
      subtotal = num(Math.max(0, Math.min(subtotalLineas, total)));
      igv = num(Math.max(0, total - subtotal));
    }
  }
  const tipo = txt(comprobante?.tipo || "FACTURA").toUpperCase();
  const ordenVentaId = txt(comprobante?.ordenVentaId);

  if (!ordenVentaId) {
    throw new Error("La factura/boleta debe registrarse obligatoriamente en base a una OS.");
  }

  const ordenVentaRef = doc(db, CONT_COLLECTIONS.ORDENES_VENTA, ordenVentaId);

  const comprobanteId = await runTransaction(db, async (transaction) => {
    const ovSnap = await transaction.get(ordenVentaRef);
    if (!ovSnap.exists()) {
      throw new Error("La OS seleccionada no existe.");
    }
    const ordenVentaData = ovSnap.data();

    const rucOv = txt(ordenVentaData.ruc_empresa);
    if (rucOv && rucOv !== ruc) {
      throw new Error("La OS seleccionada no pertenece a la empresa activa.");
    }

    await assertPeriodoAbiertoTx(transaction, ruc, comprobante?.fechaEmision);

    const saldoActual = num(
      ordenVentaData.saldo_pendiente != null
        ? ordenVentaData.saldo_pendiente
        : num(ordenVentaData.total) - num(ordenVentaData.total_facturado)
    );
    if (total > saldoActual + 0.01) {
      console.warn(`[registrarComprobanteVenta] El total del comprobante (${total.toFixed(2)}) supera el saldo pendiente de la OS (${saldoActual.toFixed(2)}).`);
    }

    const lineasOs = Array.isArray(ordenVentaData?.lineas) ? ordenVentaData.lineas : [];
    const idsLineasOs = new Set(lineasOs.map((_, idx) => `${ordenVentaId}_${idx}`));
    lineas.forEach((l, idx) => {
      const refById = txt(l?.linea_os_id);
      const refByIndice = Number.isFinite(Number(l?.indice_os)) ? Number(l.indice_os) : null;

      const tieneRefValida =
        (refById && idsLineasOs.has(refById)) ||
        (refByIndice != null && refByIndice >= 0 && refByIndice < lineasOs.length);

      if (!tieneRefValida) {
        throw new Error(`La línea ${idx + 1} de la factura no está amarrada correctamente a una línea de la OS.`);
      }
    });

    const ref = doc(collection(db, CONT_COLLECTIONS.COMPROBANTES_VENTA));

    const payload = {
      ruc_empresa: ruc,
      orden_venta_id: ordenVentaId,
      cliente_ruc: txt(comprobante?.clienteRuc),
      cliente_razon_social: txt(comprobante?.clienteRazonSocial),
      tipo,
      serie: txt(comprobante?.serie),
      numero: txt(comprobante?.numero),
      fecha_emision: txt(comprobante?.fechaEmision),
      fecha_vencimiento: txt(comprobante?.fechaVencimiento || comprobante?.fechaEmision),
      fecha_corte: txt(comprobante?.fechaCorte || ""),
      periodo_contable: txt(comprobante?.periodoContable || ""),
      moneda: txt(comprobante?.moneda || "PEN"),
      lineas,
      subtotal,
      igv,
      total,
      estado: "EMITIDO",
      estado_pago: "PENDIENTE",
      fecha_pago: "",
      numero_operacion: "",
      documentos_relacionados: ordenVentaId ? [{ tipo: "ORDEN_VENTA", referencia: ordenVentaId }] : [],
      asiento_id: "",
      estado_contable: "PENDIENTE",
      usuario_registro: txt(usuarioRegistro),
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    };

    // 1. Transaction reads and seat registration (executes period, idempotency, and CUO counter reads first)
    const asientoId = await registrarAsientoVenta({
      rucEmpresa: ruc,
      ventaId: ref.id,
      fecha: payload.fecha_emision,
      cliente: payload.cliente_razon_social || payload.cliente_ruc,
      subtotal,
      igv,
      total,
      moneda: payload.moneda,
      tipoCambio: 1,
    }, transaction);

    // 2. Transaction writes
    const docId = await registrarDocumentoComercial({
      rucEmpresa: ruc,
      tipoDocumento: tipo,
      serie: payload.serie,
      numero: payload.numero,
      fecha: payload.fecha_emision,
      monto: total,
      moneda: payload.moneda,
      moduloOrigen: "GESTION_VENTAS",
      entidadId: ref.id,
      entidadTipo: "COMPROBANTE_VENTA",
      relacionadoCon: payload.documentos_relacionados,
      metadata: {
        cliente_ruc: payload.cliente_ruc,
        cliente_razon_social: payload.cliente_razon_social,
        orden_venta_id: ordenVentaId,
      },
    }, transaction);

    await registrarDocumentoTributarioVenta({
      rucEmpresa: ruc,
      empresaId: ruc,
      origen: { id: ref.id, tipo: "VENTA", coleccion: CONT_COLLECTIONS.COMPROBANTES_VENTA },
      comprobante: {
        tipo,
        serie: payload.serie,
        numero: payload.numero,
        fechaEmision: payload.fecha_emision,
        fechaVencimiento: payload.fecha_vencimiento,
        moneda: payload.moneda,
        tipoCambio: 1,
      },
      clienteRuc: payload.cliente_ruc,
      clienteRazonSocial: payload.cliente_razon_social,
      subtotal,
      igv,
      total,
      tipoOperacion: "GRAVADO",
      usuario: usuarioRegistro,
    }, transaction);

    transaction.set(ref, {
      ...payload,
      documentos_relacionados: [
        ...payload.documentos_relacionados,
        { id: docId, tipo },
      ],
      asiento_id: asientoId,
      estado_contable: "GENERADO",
    });

    const totalFacturadoAnterior = num(ordenVentaData.total_facturado);
    const totalOv = num(ordenVentaData.total);
    const nuevoTotalFacturado = num(totalFacturadoAnterior + total);
    const nuevoSaldo = num(Math.max(0, totalOv - nuevoTotalFacturado));
    const estadoFacturacion = nuevoSaldo <= 0.01 ? "FACTURADO" : (nuevoTotalFacturado > 0 ? "PARCIAL" : "PENDIENTE");

    transaction.update(ordenVentaRef, {
      total_facturado: nuevoTotalFacturado,
      saldo_pendiente: nuevoSaldo,
      estado_facturacion: estadoFacturacion,
      estado: estadoFacturacion === "FACTURADO" ? "CERRADO" : "REGISTRADO",
      documentos_relacionados: arrayUnion({
        tipo,
        referencia: ref.id,
        serie: payload.serie,
        numero: payload.numero,
        total,
      }),
      updated_at: serverTimestamp(),
    });

    return ref.id;
  });

  return comprobanteId;
};

export const registrarPagoComprobanteVenta = async ({
  rucEmpresa,
  comprobanteId,
  fechaPago,
  numeroOperacion,
  numeroOperacionDetraccion,
  usuarioRegistro,
}) => {
  const ruc = txt(rucEmpresa);
  const id = txt(comprobanteId);
  const fecha = txt(fechaPago);
  const operacion = txt(numeroOperacion);
  const operacionDetraccion = txt(numeroOperacionDetraccion);

  if (!ruc) throw new Error("Empresa inválida para registrar pago.");
  if (!id) throw new Error("Comprobante inválido.");
  if (!fecha && !operacionDetraccion) throw new Error("Debe indicar la fecha de pago.");

  const comprobanteRef = doc(db, CONT_COLLECTIONS.COMPROBANTES_VENTA, id);
  const comprobanteSnap = await getDoc(comprobanteRef);
  if (!comprobanteSnap.exists()) {
    throw new Error("El comprobante seleccionado no existe.");
  }

  const data = comprobanteSnap.data() || {};
  if (txt(data.ruc_empresa) !== ruc) {
    throw new Error("El comprobante no pertenece a la empresa activa.");
  }

  const yaPagado = txt(data.estado_pago).toUpperCase() === "PAGADO";
  const fechaFinal = fecha || txt(data.fecha_pago);
  const operacionFinal = operacion || txt(data.numero_operacion);

  if (!fechaFinal) throw new Error("Debe indicar la fecha de pago.");
  if (!operacionFinal) throw new Error("Debe indicar el número de operación.");

  const totalComprobante = num(data.total);
  const aplicaDetraccion = totalComprobante > 700;
  const montoDetraccion = aplicaDetraccion ? num(totalComprobante * 0.12) : 0;
  const estadoDetraccion = !aplicaDetraccion
    ? "NO_APLICA"
    : (operacionDetraccion ? "REGISTRADA" : "PENDIENTE");
  const montoDetraccionPendiente = aplicaDetraccion
    ? (operacionDetraccion ? 0 : montoDetraccion)
    : 0;

  await updateDoc(comprobanteRef, {
    estado_pago: "PAGADO",
    fecha_pago: fechaFinal,
    numero_operacion: operacionFinal,
    numero_operacion_detraccion: operacionDetraccion,
    estado_detraccion: estadoDetraccion,
    monto_detraccion: montoDetraccion,
    monto_detraccion_pendiente: montoDetraccionPendiente,
    estado: "PAGADO",
    usuario_pago: txt(usuarioRegistro || data.usuario_pago),
    fecha_pago_registro_detraccion: operacionDetraccion ? serverTimestamp() : (data.fecha_pago_registro_detraccion || null),
    updated_at: serverTimestamp(),
  });

  return id;
};

export const obtenerCuentasCobrarPorEmpresa = async (rucEmpresa) => {
  const ruc = txt(rucEmpresa);
  if (!ruc) return [];

  const [ordenes, comprobantes] = await Promise.all([
    obtenerOrdenesVentaPorEmpresa(ruc),
    obtenerComprobantesVentaPorEmpresa(ruc),
  ]);

  const comprobantesPorOs = comprobantes.reduce((acc, c) => {
    const osId = txt(c.orden_venta_id);
    if (!osId) return acc;
    if (!acc[osId]) acc[osId] = [];
    acc[osId].push(c);
    return acc;
  }, {});

  return ordenes.map((os) => {
    const relacionados = comprobantesPorOs[os.id] || [];
    // Filtrar comprobantes válidos (excluir anulados por Nota de Crédito)
    const relacionadosValidos = relacionados.filter((c) => {
      const stPago = txt(c.estado_pago).toUpperCase();
      const stComp = txt(c.estado).toUpperCase();
      const esAnuladoNC =
        stPago === "CANCELADO_NC" ||
        stPago === "ANULADO" ||
        stPago === "NC_APLICADA" ||
        stComp === "ANULADO" ||
        c.anulado === true ||
        c.tiene_nc === true;
      return !esAnuladoNC;
    });

    const totalOs = num(os.total);
    const totalFacturado = num(
      relacionadosValidos.reduce((acc, c) => acc + num(c.total), 0)
    );
    const totalCobrado = num(
      relacionadosValidos
        .filter((c) => txt(c.estado_pago).toUpperCase() === "PAGADO")
        .reduce((acc, c) => acc + num(c.total), 0)
    );

    const saldoPendiente = num(Math.max(0, totalFacturado - totalCobrado));
    const saldoPorFacturar = num(Math.max(0, totalOs - totalFacturado));
    const estadoCobro = totalFacturado <= 0
      ? "SIN_FACTURAR"
      : (saldoPendiente <= 0.01 ? "COBRADO" : (totalCobrado > 0 ? "PARCIAL" : "PENDIENTE"));

    const lineasOs = Array.isArray(os.lineas)
      ? os.lineas.map((l, idx) => {
          const cantidad = num(l.cantidad);
          const precioUnitario = num(l.precio_unitario ?? l.precioUnitario);
          const importe = num(l.importe || (cantidad * precioUnitario));
          return {
            id: `${os.id}_${idx}`,
            indice: idx,
            descripcion: txt(l.descripcion),
            cantidad,
            precio_unitario: precioUnitario,
            importe,
            cantidad_facturada: 0,
            cantidad_pendiente: cantidad,
            importe_facturado: 0,
            importe_pendiente: importe,
          };
        })
      : [];

    const lineasMap = new Map(lineasOs.map((l) => [l.id, l]));
    relacionadosValidos.forEach((c) => {
      (Array.isArray(c.lineas) ? c.lineas : []).forEach((lc, idxComp) => {
        const byId = txt(lc.linea_os_id);
        const byIndice = Number.isFinite(Number(lc.indice_os)) ? Number(lc.indice_os) : null;
        let target = null;

        if (byId && lineasMap.has(byId)) {
          target = lineasMap.get(byId);
        } else if (byIndice != null) {
          target = lineasOs.find((x) => x.indice === byIndice) || null;
        } else {
          const desc = txt(lc.descripcion).toLowerCase();
          const pu = num(lc.precio_unitario ?? lc.precioUnitario);
          target = lineasOs.find((x) => x.descripcion.toLowerCase() === desc && Math.abs(num(x.precio_unitario) - pu) < 0.0001) || null;
        }

        if (!target) return;

        const cantFact = num(lc.cantidad);
        const impFact = num(lc.importe || (num(lc.cantidad) * num(lc.precio_unitario ?? lc.precioUnitario)));

        target.cantidad_facturada = num(target.cantidad_facturada + cantFact);
        target.importe_facturado = num(target.importe_facturado + impFact);
      });
    });

    lineasOs.forEach((l) => {
      l.cantidad_pendiente = num(Math.max(0, l.cantidad - l.cantidad_facturada));
      l.importe_pendiente = num(Math.max(0, l.importe - l.importe_facturado));
    });

    return {
      id: os.id,
      ruc_empresa: os.ruc_empresa,
      orden_venta_id: os.id,
      os_correlativo: txt(os.correlativo) || `OS-${os.id.slice(0, 8)}`,
      fecha_os: txt(os.fecha),
      cliente_ruc: txt(os.cliente_ruc),
      cliente_razon_social: txt(os.cliente_razon_social),
      moneda: txt(os.moneda || "PEN"),
      total_os: totalOs,
      total_facturado: totalFacturado,
      total_cobrado: totalCobrado,
      saldo_pendiente: saldoPendiente,
      saldo_por_facturar: saldoPorFacturar,
      estado_cobro: estadoCobro,
      lineas_os: lineasOs,
      comprobantes_relacionados: relacionados.map((c) => ({
        id: c.id,
        tipo: txt(c.tipo),
        serie: txt(c.serie),
        numero: txt(c.numero),
        fecha_emision: txt(c.fecha_emision),
        total: num(c.total),
        estado_pago: txt(c.estado_pago || "PENDIENTE").toUpperCase(),
        numero_operacion: txt(c.numero_operacion),
        numero_operacion_detraccion: txt(c.numero_operacion_detraccion),
        estado_detraccion: txt(c.estado_detraccion || ""),
        monto_detraccion: num(c.monto_detraccion),
        monto_detraccion_pendiente: num(c.monto_detraccion_pendiente),
        asiento_id: txt(c.asiento_id),
        estado_contable: txt(c.estado_contable || "PENDIENTE").toUpperCase(),
      })),
    };
  });
};

export const eliminarOrdenVenta = async ({ rucEmpresa, ordenVentaId }) => {
  const ruc = txt(rucEmpresa);
  const id = txt(ordenVentaId);
  if (!ruc || !id) throw new Error("ID de orden o RUC inválido.");

  const ovRef = doc(db, CONT_COLLECTIONS.ORDENES_VENTA, id);
  const ovSnap = await getDoc(ovRef);
  if (!ovSnap.exists()) throw new Error("La orden de venta no existe.");
  const data = ovSnap.data();

  if (txt(data.ruc_empresa) !== ruc) throw new Error("La orden no pertenece a esta empresa.");

  // Verificar si tiene comprobantes facturados
  const totalFacturado = num(data.total_facturado);
  if (totalFacturado > 0) {
    throw new Error("No se puede eliminar una orden que ya tiene comprobantes facturados. Elimine primero los comprobantes.");
  }

  // 1. Eliminar documentos comerciales relacionados
  const qDoc = query(
    collection(db, CONT_COLLECTIONS.DOCUMENTOS_COMERCIALES),
    where("entidad_id", "==", id),
    where("entidad_tipo", "==", "ORDEN_VENTA")
  );
  const snapDoc = await getDocs(qDoc);
  for (const d of snapDoc.docs) {
    await deleteDoc(d.ref);
  }

  // 2. Eliminar la orden
  await deleteDoc(ovRef);
};

export const actualizarOrdenVenta = async ({ rucEmpresa, ordenId, orden, usuarioRegistro }) => {
  const ruc = txt(rucEmpresa);
  const id = txt(ordenId);
  if (!ruc || !id) throw new Error("ID de orden o RUC inválido.");

  const ovRef = doc(db, CONT_COLLECTIONS.ORDENES_VENTA, id);
  const ovSnap = await getDoc(ovRef);
  if (!ovSnap.exists()) throw new Error("La orden de venta no existe.");
  const oldData = ovSnap.data();

  // No permitir editar si ya está facturada (por simplicidad y seguridad de saldos)
  if (num(oldData.total_facturado) > 0) {
    throw new Error("No se puede editar una orden que ya tiene comprobantes facturados. Anule los comprobantes primero.");
  }

  const lineas = Array.isArray(orden?.lineas)
    ? orden.lineas.map((l, idx) => ({
        descripcion: txt(l?.descripcion),
        indice: idx,
        cantidad: num(l?.cantidad),
        precio_unitario: num(l?.precioUnitario ?? l?.precio_unitario),
        importe: num(l?.importe || (num(l?.cantidad) * num(l?.precioUnitario ?? l?.precio_unitario))),
      })).filter(l => l.descripcion && l.cantidad > 0)
    : [];
  
  const total = lineas.reduce((acc, l) => acc + l.importe, 0);

  await updateDoc(ovRef, {
    fecha: txt(orden.fecha),
    cliente_ruc: txt(orden.clienteRuc),
    cliente_razon_social: txt(orden.clienteRazonSocial),
    descripcion: txt(orden.descripcion),
    moneda: txt(orden.moneda || "PEN"),
    lineas,
    total: num(total),
    saldo_pendiente: num(total),
    usuario_ultimo_cambio: txt(usuarioRegistro),
    updated_at: serverTimestamp(),
  });

  // Actualizar documento comercial relacionado si existe
  const qDoc = query(
    collection(db, CONT_COLLECTIONS.DOCUMENTOS_COMERCIALES),
    where("entidad_id", "==", id),
    where("entidad_tipo", "==", "ORDEN_VENTA")
  );
  const snapDoc = await getDocs(qDoc);
  for (const d of snapDoc.docs) {
    await updateDoc(d.ref, {
      fecha: txt(orden.fecha),
      monto: num(total),
      moneda: txt(orden.moneda || "PEN"),
      "metadata.cliente_ruc": txt(orden.clienteRuc),
      "metadata.cliente_razon_social": txt(orden.clienteRazonSocial),
      updated_at: serverTimestamp(),
    });
  }
};

export const eliminarComprobanteVenta = async ({ rucEmpresa, comprobanteId }) => {
  const ruc = txt(rucEmpresa);
  const id = txt(comprobanteId);
  if (!ruc || !id) throw new Error("ID de comprobante o RUC inválido.");

  const compRef = doc(db, CONT_COLLECTIONS.COMPROBANTES_VENTA, id);
  const compSnap = await getDoc(compRef);
  if (!compSnap.exists()) throw new Error("El comprobante no existe.");
  const data = compSnap.data();

  if (txt(data.ruc_empresa) !== ruc) throw new Error("El comprobante no pertenece a esta empresa.");

  // 1. Revertir saldos en la OS
  const ordenVentaId = txt(data.orden_venta_id);
  if (ordenVentaId) {
    const ovRef = doc(db, CONT_COLLECTIONS.ORDENES_VENTA, ordenVentaId);
    const ovSnap = await getDoc(ovRef);
    if (ovSnap.exists()) {
      const ovData = ovSnap.data();
      const totalComprobante = num(data.total);
      const facturadoAnterior = num(ovData.total_facturado);
      const totalOv = num(ovData.total);
      
      const nuevoTotalFacturado = num(Math.max(0, facturadoAnterior - totalComprobante));
      const nuevoSaldo = num(Math.max(0, totalOv - nuevoTotalFacturado));
      const estadoFacturacion = nuevoSaldo <= 0.01 ? "FACTURADO" : (nuevoTotalFacturado > 0 ? "PARCIAL" : "PENDIENTE");

      const relacionados = Array.isArray(ovData.documentos_relacionados) ? ovData.documentos_relacionados : [];
      const nuevosRelacionados = relacionados.filter(r => r.referencia !== id && r.id !== id);

      await updateDoc(ovRef, {
        total_facturado: nuevoTotalFacturado,
        saldo_pendiente: nuevoSaldo,
        estado_facturacion: estadoFacturacion,
        estado: estadoFacturacion === "FACTURADO" ? "CERRADO" : "REGISTRADO",
        documentos_relacionados: nuevosRelacionados,
        updated_at: serverTimestamp()
      });
    }
  }

  // 2. Eliminar documentos comerciales
  const qDoc = query(
    collection(db, CONT_COLLECTIONS.DOCUMENTOS_COMERCIALES),
    where("entidad_id", "==", id),
    where("entidad_tipo", "==", "COMPROBANTE_VENTA")
  );
  const snapDoc = await getDocs(qDoc);
  for (const d of snapDoc.docs) {
    await deleteDoc(d.ref);
  }

  // 3. Eliminar asientos
  const qAsiento = query(
    collection(db, CONT_COLLECTIONS.ASIENTOS),
    where("origen_id", "==", id),
    where("origen", "==", "VENTA")
  );
  const snapAsiento = await getDocs(qAsiento);
  for (const d of snapAsiento.docs) {
    await deleteDoc(d.ref);
  }

  // 4. Eliminar el comprobante
  await deleteDoc(compRef);
};

export const actualizarComprobanteVenta = async ({ rucEmpresa, comprobanteId, comprobante, usuarioRegistro }) => {
    // Por simplicidad en esta fase, si cambia la OS, es mejor borrar y rehacer.
    // Aquí implementaremos actualización de campos básicos (numero, serie, fecha).
    const id = txt(comprobanteId);
    const ref = doc(db, CONT_COLLECTIONS.COMPROBANTES_VENTA, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("No existe el comprobante.");
    const old = snap.data();

    if (txt(old.orden_venta_id) !== txt(comprobante.ordenVentaId)) {
        throw new Error("No se puede cambiar la OS de una factura existente. Por favor elimínela y regístrela nuevamente con la OS correcta.");
    }

    await updateDoc(ref, {
        serie: txt(comprobante.serie),
        numero: txt(comprobante.numero),
        fecha_emision: txt(comprobante.fechaEmision),
        fecha_vencimiento: txt(comprobante.fechaVencimiento || comprobante.fechaEmision),
        fecha_corte: txt(comprobante.fechaCorte || ""),
        periodo_contable: txt(comprobante.periodoContable || ""),
        updated_at: serverTimestamp(),
        usuario_edicion: txt(usuarioRegistro)
    });

    // Actualizar documento comercial
    const qDoc = query(
        collection(db, CONT_COLLECTIONS.DOCUMENTOS_COMERCIALES),
        where("entidad_id", "==", id),
        where("entidad_tipo", "==", "COMPROBANTE_VENTA")
    );
    const snapDoc = await getDocs(qDoc);
    for (const d of snapDoc.docs) {
        await updateDoc(d.ref, {
            serie: txt(comprobante.serie),
            numero: txt(comprobante.numero),
            fecha: txt(comprobante.fechaEmision),
            updated_at: serverTimestamp(),
        });
    }
};

export const anularComprobanteVentaConNC = async ({ rucEmpresa, comprobanteId, serieTarget, numeroTarget, serieNc, numeroNc, motivo, usuarioRegistro }) => {
  const ruc = txt(rucEmpresa);
  const id = txt(comprobanteId);
  const sTarget = txt(serieTarget);
  const nTarget = txt(numeroTarget);
  const sNc = txt(serieNc).toUpperCase();
  const nNc = txt(numeroNc);

  if (!ruc) throw new Error("RUC de empresa no válido.");
  if (!sNc || !nNc) throw new Error("Debe ingresar la serie y el número de la Nota de Crédito.");

  let ref = null;
  if (id) {
    const directRef = doc(db, CONT_COLLECTIONS.COMPROBANTES_VENTA, id);
    const snapDirect = await getDoc(directRef);
    if (snapDirect.exists()) {
      ref = directRef;
    }
  }

  if (!ref && sTarget && nTarget) {
    const qComp = query(
      collection(db, CONT_COLLECTIONS.COMPROBANTES_VENTA),
      where("ruc_empresa", "==", ruc),
      where("serie", "==", sTarget),
      where("numero", "==", nTarget)
    );
    const snapComp = await getDocs(qComp);
    if (!snapComp.empty) {
      ref = snapComp.docs[0].ref;
    }
  }

  if (!ref) {
    // Intentar buscar en todos los comprobantes de la empresa si id es clave compuesta
    const qAll = query(
      collection(db, CONT_COLLECTIONS.COMPROBANTES_VENTA),
      where("ruc_empresa", "==", ruc)
    );
    const snapAll = await getDocs(qAll);
    const foundDoc = snapAll.docs.find((d) => {
      const data = d.data();
      const s = txt(data.serie);
      const n = txt(data.numero);
      return d.id === id || `${s}-${n}` === `${sTarget}-${nTarget}` || n === nTarget;
    });
    if (foundDoc) {
      ref = foundDoc.ref;
    }
  }

  if (!ref) throw new Error("No se encontró la factura a anular en la base de datos.");

  const ncRef = `${sNc}-${nNc}`;

  const compSnap = await getDoc(ref);
  const compData = compSnap.exists() ? compSnap.data() : {};
  const realCompId = compSnap.id;

  await updateDoc(ref, {
    estado_pago: "CANCELADO_NC",
    estado: "ANULADO",
    tiene_nc: true,
    anulado: true,
    nota_credito_referencia: ncRef,
    nota_credito_id: ncRef,
    nota_credito_motivo: txt(motivo || "Anulación de comprobante por Nota de Crédito"),
    fecha_anulacion_nc: serverTimestamp(),
    usuario_anulacion: txt(usuarioRegistro),
    updated_at: serverTimestamp(),
  });

  // Restaurar saldos de la Orden de Venta (OS)
  const ordenVentaId = txt(compData.orden_venta_id);
  if (ordenVentaId) {
    const ovRef = doc(db, CONT_COLLECTIONS.ORDENES_VENTA, ordenVentaId);
    const ovSnap = await getDoc(ovRef);
    if (ovSnap.exists()) {
      const ovData = ovSnap.data();
      const totalComprobante = num(compData.total);
      const facturadoAnterior = num(ovData.total_facturado);
      const totalOv = num(ovData.total);

      const nuevoTotalFacturado = num(Math.max(0, facturadoAnterior - totalComprobante));
      const nuevoSaldo = num(Math.max(0, totalOv - nuevoTotalFacturado));
      const estadoFacturacion = nuevoSaldo <= 0.01 ? "FACTURADO" : (nuevoTotalFacturado > 0 ? "PARCIAL" : "PENDIENTE");

      await updateDoc(ovRef, {
        total_facturado: nuevoTotalFacturado,
        saldo_pendiente: nuevoSaldo,
        estado_facturacion: estadoFacturacion,
        estado: estadoFacturacion === "FACTURADO" ? "CERRADO" : "REGISTRADO",
        updated_at: serverTimestamp()
      });
    }
  }

  // Actualizar documentos comerciales vinculados
  const qDoc = query(
    collection(db, CONT_COLLECTIONS.DOCUMENTOS_COMERCIALES),
    where("entidad_id", "==", realCompId),
    where("entidad_tipo", "==", "COMPROBANTE_VENTA")
  );
  const snapDoc = await getDocs(qDoc);
  for (const d of snapDoc.docs) {
    await updateDoc(d.ref, {
      estado_pago: "CANCELADO_NC",
      estado: "ANULADO",
      nota_credito_referencia: ncRef,
      updated_at: serverTimestamp(),
    });
  }

  return { success: true, comprobanteId: realCompId, ncRef };
};

